import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { debugLogger } from '@/utils/debugLogger';
import { toast } from 'sonner';

export type ReportReason = 
  | 'fake_profile'
  | 'harassment'
  | 'spam'
  | 'inappropriate_content'
  | 'scam'
  | 'other';

export const REPORT_REASONS: { value: ReportReason; label: string; emoji: string }[] = [
  { value: 'fake_profile', label: 'Perfil falso', emoji: '🚫' },
  { value: 'harassment', label: 'Assédio ou comportamento ofensivo', emoji: '😠' },
  { value: 'spam', label: 'Spam ou propaganda', emoji: '📧' },
  { value: 'inappropriate_content', label: 'Conteúdo inapropriado', emoji: '🔞' },
  { value: 'scam', label: 'Golpe ou fraude', emoji: '💰' },
  { value: 'other', label: 'Outro', emoji: '❓' }
];

interface ReportState {
  hasReported: boolean;
  isLoading: boolean;
}

export function useReportUser(targetUserId: string | undefined) {
  const { user } = useAuth();
  const [state, setState] = useState<ReportState>({
    hasReported: false,
    isLoading: false
  });

  const checkReportStatus = useCallback(async () => {
    if (!user?.id || !targetUserId) return;

    try {
      const { data } = await supabase
        .from('user_reports')
        .select('id')
        .eq('reporter_id', user.id)
        .eq('reported_id', targetUserId)
        .maybeSingle();

      setState(prev => ({ ...prev, hasReported: !!data }));
    } catch (error) {
      debugLogger.error('[useReportUser] Error checking report status:', error);
    }
  }, [user?.id, targetUserId]);

  const reportUser = useCallback(async (reason: ReportReason, description?: string) => {
    if (!user?.id || !targetUserId) return false;

    setState(prev => ({ ...prev, isLoading: true }));

    try {
      const { error } = await supabase
        .from('user_reports')
        .insert({
          reporter_id: user.id,
          reported_id: targetUserId,
          reason,
          description
        });

      if (error) throw error;

      setState(prev => ({ ...prev, hasReported: true, isLoading: false }));
      toast.success('Denúncia enviada. Nossa equipe irá analisar.');
      debugLogger.info('[useReportUser] User reported:', { targetUserId, reason });
      return true;
    } catch (error) {
      debugLogger.error('[useReportUser] Error reporting user:', error);
      toast.error('Erro ao enviar denúncia');
      setState(prev => ({ ...prev, isLoading: false }));
      return false;
    }
  }, [user?.id, targetUserId]);

  return {
    ...state,
    checkReportStatus,
    reportUser
  };
}
